import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker, sendReferral } from '../api/worker';
import { Referral, SendReferralRequest } from '../types';
import toast from 'react-hot-toast';

export const useReferrals = (nodeId: string) => {
  return useQuery<Referral[]>({
    queryKey: ['referrals', nodeId],
    queryFn: async () => {
      if (!nodeId) return [];
      const response = await apiWorker.get(`/api/nodes/${nodeId}/referrals`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!nodeId
  });
};

export const useGenerateReferral = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiWorker.post(`/api/nodes/${nodeId}/referrals`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referrals', nodeId] });
      toast.success('Referral code generated successfully');
    },
    onError: () => {
      toast.error('Failed to generate referral code');
    }
  });
};

export const useValidateReferral = (code: string) => {
  return useQuery<Referral>({
    queryKey: ['referral-validate', code],
    queryFn: async () => {
      if (!code) return null as any;
      const response = await apiWorker.get(`/api/referrals/validate/${code}`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!code,
    retry: false
  });
};

export const useSendReferralEmail = (nodeId: string, code: string) => {
  return useMutation({
    mutationFn: async (input: string[] | { emails: string[]; personalMessage?: string }) => {
      const emails = Array.isArray(input) ? input : input.emails;
      const personalMessage = Array.isArray(input) ? undefined : input.personalMessage;
      await Promise.all(
        emails.map(email => sendReferral(nodeId, code, { email, ...(personalMessage ? { personalMessage } : {}) } as SendReferralRequest))
      );
    },
    onSuccess: () => {
      toast.success('Referral invitations sent successfully!');
    },
    onError: () => {
      toast.error('Failed to send referral invitations.');
    }
  });
};
